import axios from 'axios'
import sessionService from './sessionService.js'

// Servicio de autenticación para el frontend
class AuthService {
  constructor() {
    this.baseURL = import.meta?.env?.VITE_API_BASE_URL || '/api'
    this.adminMode = false // Modo por defecto: app normal
    
    // Claves de localStorage (se configurarán según el modo)
    this.tokenKey = 'ldda_auth_token'
    this.userKey = 'ldda_user_data'
    this.anonymousTokenKey = 'ldda_anonymous_token'

    // Cliente axios específico para auth (sin interceptores para evitar loops)
    this.authClient = axios.create({
      baseURL: this.baseURL,
      timeout: 5000,
      headers: {
        'Content-Type': 'application/json',
      }
    })

    // NO inicializar token anónimo aquí - se hará después de configurar el modo
    // Por defecto configurar en modo app si no se especifica otra cosa
    this.adminMode = false
  }

  // Configurar el servicio para modo admin
  setAdminMode(isAdmin = true) {
    this.adminMode = isAdmin
    if (isAdmin) {
      this.tokenKey = 'ldda_admin_auth_token'
      this.userKey = 'ldda_admin_user_data'
      // En modo admin no usamos tokens anónimos
      console.log('🔧 AuthService configurado para modo ADMIN')
    } else {
      this.tokenKey = 'ldda_auth_token'
      this.userKey = 'ldda_user_data'
      this.anonymousTokenKey = 'ldda_anonymous_token'
      console.log('🔧 AuthService configurado para modo APP')
      
      // Inicializar token anónimo solo en modo app
      this.initializeAnonymousToken()
    }
  }

  // Verificar si está en modo admin
  isAdminMode() {
    return this.adminMode
  }

  // Obtener token del localStorage (prioriza token de usuario sobre anónimo)
  getToken() {
    return localStorage.getItem(this.tokenKey) || localStorage.getItem(this.anonymousTokenKey)
  }

  // Obtener específicamente token anónimo
  getAnonymousToken() {
    return localStorage.getItem(this.anonymousTokenKey)
  }

  // Guardar token anónimo
  setAnonymousToken(token) {
    localStorage.setItem(this.anonymousTokenKey, token)
  }

  // Solicitar token anónimo al servidor
  async requestAnonymousToken() {
    try {
      console.log('🔄 Solicitando token anónimo a:', this.baseURL + '/auth/anonymous')

      // Incluir sessionId en la solicitud
      const sessionId = sessionService.getSessionId()
      console.log('🆔 Incluyendo sessionId en token:', sessionId)

      const response = await this.authClient.post('/auth/anonymous', {
        app: 'ldda-app',
        sessionId: sessionId
      })

      console.log('📡 Respuesta del servidor:', response.status, response.data)

      if (response.data.success && response.data.token) {
        this.setAnonymousToken(response.data.token)
        console.log('🔑 Token anónimo obtenido exitosamente')
        return response.data.token
      } else {
        console.error('❌ Respuesta del servidor no válida:', response.data)
        return null
      }

      return null
    } catch (error) {
      console.error('❌ Error obteniendo token anónimo:', error.response?.data || error.message)
      console.error('❌ URL intentada:', this.baseURL + '/auth/anonymous')
      console.error('❌ Status:', error.response?.status)
      return null
    }
  }

  // Inicializar token anónimo si no existe (solo en modo app)
  async initializeAnonymousToken() {
    // No inicializar tokens anónimos en modo admin
    if (this.adminMode) {
      console.log('🔐 Modo admin: tokens anónimos deshabilitados')
      return
    }

    const userToken = localStorage.getItem(this.tokenKey)
    
    // Si hay token de usuario, no necesita token anónimo
    if (userToken) {
      console.log('🔐 Usuario autenticado, token anónimo no necesario')
      return
    }
    
    // Siempre obtener/refrescar token anónimo con partidaCode actual
    console.log('🔄 Obteniendo/refrescando token anónimo...')
    await this.requestAnonymousToken()
  }  // Verificar si tiene token anónimo
  hasAnonymousToken() {
    return !!this.getAnonymousToken()
  }

  // Guardar token en localStorage
  setToken(token) {
    localStorage.setItem(this.tokenKey, token)
  }

  // Eliminar token del localStorage
  removeToken() {
    localStorage.removeItem(this.tokenKey)
    localStorage.removeItem(this.userKey)
    // NO eliminar el token anónimo, puede seguir siendo útil
  }

  // Eliminar todos los tokens incluyendo anónimo
  removeAllTokens() {
    localStorage.removeItem(this.tokenKey)
    localStorage.removeItem(this.userKey)
    localStorage.removeItem(this.anonymousTokenKey)
  }

  // Eliminar cookie por nombre
  removeCookie(name) {
    // Eliminar cookie con diferentes paths para asegurar que se borre
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/admin;`
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/api;`
    console.log(`🍪 Cookie '${name}' eliminada`)
  }

  // Obtener datos del usuario
  getUser() {
    const userData = localStorage.getItem(this.userKey)
    return userData ? JSON.parse(userData) : null
  }

  // Guardar datos del usuario
  setUser(user) {
    localStorage.setItem(this.userKey, JSON.stringify(user))
  }

  // Verificar si el usuario está autenticado (incluyendo token anónimo)
  isAuthenticated() {
    const token = this.getToken()
    if (!token) return false

    try {
      // Verificar si el token no ha expirado (básico)
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Date.now() / 1000
      return payload.exp > currentTime
    } catch (error) {
      console.error('Error verificando token:', error)
      return false
    }
  }

  // Verificar si el usuario tiene autenticación completa (no anónima)
  isFullyAuthenticated() {
    const userToken = localStorage.getItem(this.tokenKey)
    return userToken && this.isAuthenticated()
  }

  // Verificar si solo tiene token anónimo
  isAnonymousOnly() {
    const userToken = localStorage.getItem(this.tokenKey)
    const anonymousToken = this.getAnonymousToken()
    return !userToken && !!anonymousToken
  }

  // Verificar si el usuario es administrador
  isAdmin() {
    const user = this.getUser()
    return user && user.role === 'admin'
  }

  // Login
  async login(username, password) {
    try {
      const response = await this.authClient.post('/auth/login', {
        username,
        password
      })

      const data = response.data

      // En modo admin, verificar que el usuario no es anónimo
      if (this.adminMode && (!data.user || data.user.role === 'anonymous')) {
        throw new Error('Acceso denegado: Un usuario anónimo no puede entrar')
      }

      // Guardar token y datos del usuario
      this.setToken(data.token)
      this.setUser(data.user)

      // Notificar cambio de usuario (para componentes reactivos)
      this.notifyUserChange()

      // Esperar un breve momento para asegurar que el token está disponible
      // en el localStorage para los interceptores
      await new Promise(resolve => setTimeout(resolve, 100))

      return data
    } catch (error) {
      console.error('Error en login:', error)
      throw new Error(error.response?.data?.error || 'Error en el login')
    }
  }

  // Notificar cambios de usuario (para componentes reactivos)
  notifyUserChange() {
    if (typeof window !== 'undefined') {
      const eventName = this.adminMode ? 'admin-user-updated' : 'user-updated'
      window.dispatchEvent(new CustomEvent(eventName))
    }
  }

  // Logout
  async logout(router) {
    try {
      const token = this.getToken()
      if (token) {
        // Notificar al servidor (opcional)
        await this.authClient.post('/auth/logout', {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      }
    } catch (error) {
      console.error('Error en logout:', error)
    } finally {
      console.log('🧹 Iniciando limpieza en logout...')
      
      // Limpiar datos locales siempre
      this.removeToken()
      console.log('🗑️ Token eliminado del localStorage')
      
      // Limpiar cookie de sesión
      this.removeCookie('ldda-session-id')
      console.log('🍪 Cookie de sesión eliminada')
      
      // Verificar que se limpió correctamente
      const tokenAfterClean = this.getToken()
      const userAfterClean = this.getUser()
      console.log('🔍 Verificación post-limpieza - Token:', !!tokenAfterClean, 'User:', !!userAfterClean)
      
      // Notificar cambio de usuario
      this.notifyUserChange()
      console.log('📢 Evento de usuario actualizado disparado')

      // Redirigir al login usando Vue Router
      if (router) {
        console.log('🔄 Redirigiendo a login...')
        try {
          await router.push({ name: 'login', force: true })
          console.log('✅ Redirección completada')
        } catch (error) {
          console.error('❌ Error en redirección:', error)
        }
      }
    }
  }

  // Verificar token con el servidor
  async verifyToken() {
    try {
      const token = this.getToken()
      const response = await this.authClient.post('/auth/verify', {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      const data = response.data

      // Actualizar datos del usuario
      this.setUser(data.user)
      return data
    } catch (error) {
      console.error('Error verificando token:', error)
      this.removeToken()
      throw new Error(error.response?.data?.error || 'Token inválido')
    }
  }

  // Obtener perfil del usuario
  async getProfile() {
    try {
      const token = this.getToken()
      const response = await this.authClient.get('/auth/profile', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      const data = response.data

      this.setUser(data.user)
      return data
    } catch (error) {
      console.error('Error obteniendo perfil:', error)
      throw new Error(error.response?.data?.error || 'Error obteniendo perfil')
    }
  }

  // Cambiar contraseña
  async changePassword(currentPassword, newPassword) {
    try {
      const token = this.getToken()
      const response = await this.authClient.put('/auth/change-password', {
        currentPassword,
        newPassword
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      return response.data
    } catch (error) {
      console.error('Error cambiando contraseña:', error)
      throw new Error(error.response?.data?.error || 'Error cambiando contraseña')
    }
  }

  // Hacer petición HTTP autenticada (compatibilidad con fetch)
  async makeAuthenticatedRequest(url, options = {}) {
    // Importar dinámicamente para evitar dependencias circulares
    const { default: apiClient } = await import('./apiClient.js')

    try {
      // Convertir la URL a un path relativo si es absoluta
      const urlPath = url.replace(this.baseURL, '')

      // Convertir opciones de fetch a axios
      const method = (options.method || 'GET').toLowerCase()
      const axiosOptions = {
        method,
        url: urlPath
      }

      // Añadir body si existe
      if (options.body) {
        axiosOptions.data = typeof options.body === 'string' ? JSON.parse(options.body) : options.body
      }

      // Añadir headers adicionales
      if (options.headers) {
        axiosOptions.headers = options.headers
      }

      const response = await apiClient(axiosOptions)

      // Crear un objeto similar a fetch Response para compatibilidad
      return {
        ok: response.status >= 200 && response.status < 300,
        status: response.status,
        json: async () => response.data,
        data: response.data
      }
    } catch (error) {
      location.href = '/admin';
      throw error
    }
  }

  // Asegurar que tenemos un token válido (usuario o anónimo)
  async ensureAuthenticated() {
    try {
      let token = this.getToken()

      // Si no hay token, intentar obtener uno anónimo
      if (!token) {
        console.log('🔄 No hay token, obteniendo token anónimo...')
        token = await this.requestAnonymousToken()

        if (!token) {
          throw new Error('No se pudo obtener token de autenticación')
        }
      }

      console.log('✅ Token de autenticación disponible:', this.isAnonymousOnly() ? 'anónimo' : 'usuario')
      return token

    } catch (error) {
      console.error('❌ Error asegurando autenticación:', error)
      throw error
    }
  }
}

// Crear instancia única del servicio
const authService = new AuthService()

export default authService
